import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search as SearchIcon, X as ClearIcon } from 'lucide-react';

// =============================================================================
// SearchBar — v2 design-system search input primitive.
//
// Native <input> (intentionally not AntD Input.Search) so the v2 look is
// owned by our SCSS and not subject to AntD's affix-wrapper internals.
// Governed by `_design-system.scss .ds-searchbar`.
//
// Modes:
//   - Controlled   — pass `value` + `onChange`. Parent owns the string.
//   - Uncontrolled — omit `value` (component stores it internally). Use
//                    `defaultValue` to seed.
//
// Two change callbacks, fire at different moments:
//   - onChange(event) — every keystroke. Event matches native <input> so
//                       consumers can `e.target.value` it.
//   - onSearch(value) — DEBOUNCED by `debounceMs` (default 300). Fires with
//                       the string directly. Use this to trigger the query.
//                       Set `debounceMs={0}` to fire on every keystroke.
//
// Variants:
//   - inline   — filter-style bar above tables (the common case).
//   - launcher — pill with trailing kbd hint (e.g. ⌘K global palette trigger).
//   - ghost    — borderless, for embedding in toolbars/headers.
// =============================================================================

const SearchBar = React.forwardRef(function SearchBar(props, ref) {
  const {
    value,
    defaultValue = '',
    onChange,
    onSearch,
    debounceMs = 300,
    placeholder = 'Search…',
    allowClear = true,
    size = 'md',
    variant = 'inline',
    trailing = null,
    disabled = false,
    autoFocus = false,
    className = '',
    style,
    inputProps = {},
    'aria-label': ariaLabel,
  } = props;

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  const innerRef = useRef(null);
  const setRef = useCallback(
    (node) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  // Debounced onSearch. Skip the initial fire on mount — we only want to
  // fire in response to user input, not echo the default value back out.
  const lastFiredRef = useRef(currentValue);
  useEffect(() => {
    if (!onSearch) return undefined;
    if (currentValue === lastFiredRef.current) return undefined;
    if (debounceMs <= 0) {
      lastFiredRef.current = currentValue;
      onSearch(currentValue);
      return undefined;
    }
    const timer = setTimeout(() => {
      lastFiredRef.current = currentValue;
      onSearch(currentValue);
    }, debounceMs);
    return () => clearTimeout(timer);
  }, [currentValue, debounceMs, onSearch]);

  const handleChange = (event) => {
    if (!isControlled) setInternalValue(event.target.value);
    if (onChange) onChange(event);
  };

  const handleClear = () => {
    if (!isControlled) setInternalValue('');
    if (onChange) {
      // Synthesize a minimal change event so controlled parents see the clear.
      const synthetic = { target: { value: '' }, currentTarget: { value: '' } };
      onChange(synthetic);
    }
    if (innerRef.current) innerRef.current.focus();
  };

  const hasValue = currentValue != null && String(currentValue).length > 0;
  const showClear = allowClear && hasValue && !disabled;

  const rootClass = [
    'ds-searchbar',
    `ds-searchbar--${variant}`,
    `ds-searchbar--${size}`,
    disabled ? 'ds-searchbar--disabled' : '',
    hasValue ? 'ds-searchbar--has-value' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = size === 'sm' ? 14 : size === 'lg' ? 18 : 16;

  return (
    <div className={rootClass} style={style} role="search">
      <SearchIcon
        className="ds-searchbar__icon"
        width={iconSize}
        height={iconSize}
        aria-hidden="true"
      />
      <input
        {...inputProps}
        ref={setRef}
        type="search"
        className="ds-searchbar__input"
        value={currentValue ?? ''}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel || placeholder}
      />
      {showClear && (
        <button
          type="button"
          className="ds-searchbar__clear"
          onClick={handleClear}
          aria-label="Clear search"
          tabIndex={-1}
        >
          <ClearIcon width={iconSize} height={iconSize} aria-hidden="true" />
        </button>
      )}
      {trailing && <span className="ds-searchbar__trailing">{trailing}</span>}
    </div>
  );
});

export default SearchBar;
